import { useCallback, useEffect, useState } from "react";
import { MonitorSmartphone, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" }) + " WIB" : "-");

const Row = ({ item, kind, onRevoke }) => (
  <li className="flex items-start justify-between gap-3 py-3" data-testid={`${kind}-row-${item.id}`}>
    <div className="text-sm">
      <p className="font-semibold text-slate-900">{item.deviceLabel || "Perangkat"} {item.current && <span className="ml-1 text-xs text-emerald-800 font-bold">(perangkat ini)</span>}</p>
      <p className="text-xs text-slate-500">IP {item.ip || "-"} · {kind === "session" ? `Aktif terakhir ${fmt(item.lastSeenAt)}` : `Dipercaya sampai ${fmt(item.expiresAt)}`}</p>
    </div>
    <Button size="sm" variant="outline" className="text-red-700 shrink-0" onClick={() => onRevoke(item)} data-testid={`${kind}-revoke-${item.id}`}>
      {kind === "session" ? "Akhiri" : "Cabut"}
    </Button>
  </li>
);

export const DevicesCard = () => {
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (path, item, msg) => {
    try {
      const { data: r } = await api.delete(`${path}/${item.id}`);
      toast.success(msg);
      if (r.current) setUser(false);
      else load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const logoutAll = async () => {
    try {
      await api.post("/auth/logout-all");
      toast.success("Semua perangkat telah dikeluarkan");
      setUser(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 lg:col-span-2 space-y-4" data-testid="devices-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><MonitorSmartphone className="h-5 w-5 text-emerald-800" /> Perangkat & Sesi</h3>
          <p className="text-xs text-slate-500 mt-1" data-testid="devices-otp-policy">
            {data?.otpRequired ? `Login dari perangkat baru memerlukan kode verifikasi email. Perangkat terpercaya berlaku ${data.trustDays} hari.` : "Akun ini masuk dengan kata sandi tanpa kode verifikasi."}
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="text-red-700" data-testid="logout-all-btn"><LogOut className="h-4 w-4 mr-2" /> Keluar dari Semua Perangkat</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Keluar dari semua perangkat?</AlertDialogTitle>
              <AlertDialogDescription>Semua sesi aktif (termasuk perangkat ini) akan diakhiri dan semua perangkat terpercaya dicabut. Login berikutnya memerlukan kode verifikasi email.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="logout-all-cancel">Batal</AlertDialogCancel>
              <AlertDialogAction onClick={logoutAll} className="bg-red-700 hover:bg-red-800" data-testid="logout-all-confirm">Ya, Keluarkan Semua</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Sesi aktif ({data?.sessions.length ?? 0})</p>
          <ul className="divide-y divide-slate-100" data-testid="sessions-list">
            {data?.sessions.map((s) => <Row key={s.id} item={s} kind="session" onRevoke={(i) => revoke("/auth/sessions", i, "Sesi diakhiri")} />)}
          </ul>
        </section>
        {data?.otpRequired && <section>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Perangkat terpercaya ({data?.devices.length ?? 0})</p>
          <ul className="divide-y divide-slate-100" data-testid="devices-list">
            {data?.devices.map((d) => <Row key={d.id} item={d} kind="device" onRevoke={(i) => revoke("/auth/devices", i, "Perangkat dicabut dan sesinya diakhiri")} />)}
            {data && !data.devices.length && <li className="py-3 text-sm text-slate-500" data-testid="devices-empty">Belum ada perangkat terpercaya.</li>}
          </ul>
        </section>}
      </div>
    </div>
  );
};
